import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getAuthHeaders, useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';
import { API_BASE } from '../config/api';

const API = API_BASE;

const FeatureContext = createContext(null);

// ---------------------------------------------------------------------------
// PHASE_0_3_BUILD_SPEC.md Task B (step 5): this context previously called the
// legacy user-scoped GET /api/user/features endpoint, which reads
// db.subscriptions — a table no checkout flow writes to anymore. That meant
// isPro/isStudio/features could stay stale indefinitely after a real upgrade.
//
// It now sources from the WORKSPACE-scoped entitlements endpoint
// (GET /api/entitlements/workspace/{id}) for the currently selected
// workspace — the same source of truth the backend enforces with. The hook
// API surface (useFeatures/useFeature/useLimit) is unchanged so consumers
// (Home, WorkspaceHome, SceneBoard, Sidebar) keep working.
// ---------------------------------------------------------------------------

// plan_id -> tier ('free' | 'pro' | 'studio'), version-agnostic (v1 and v2
// ids map to the same tier). Mirrors backend pricing.get_plan_tier.
const PLAN_ID_TO_TIER = {
  plan_free: 'free',
  plan_pro: 'pro',
  plan_pro_v2: 'pro',
  plan_studio: 'studio',
  plan_studio_v2: 'studio',
};

const FREE_FALLBACK_FEATURES = {
  active_film_limit: 1,
  seat_limit: 1,
  max_storage_gb: 1,
  script_upload: false,
  collaboration: false,
  can_export: false,
  advanced_analytics: false,
  additional_seats_allowed: false,
  ai_insights: false,
  governance: 'none',
};

export const FeatureProvider = ({ children }) => {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace() || {};
  const [plan, setPlan] = useState(null);
  const [features, setFeatures] = useState(null);
  const [usage, setUsage] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [billing, setBilling] = useState(null);
  const [overLimits, setOverLimits] = useState(null);
  const [trial, setTrial] = useState(null);
  const [aiCredits, setAiCredits] = useState(null);
  const [featureFlags, setFeatureFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userId = user?.id;
  const workspaceId = currentWorkspace?.id;

  const fetchWorkspaceEntitlements = useCallback(async () => {
    if (!userId || !workspaceId) {
      setPlan(null);
      setFeatures(null);
      setUsage(null);
      setEntitlements(null);
      setFeatureFlags({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/entitlements/workspace/${workspaceId}`,
        { headers: getAuthHeaders() }
      );
      const data = response.data || {};
      // During a trial (Phase 2 Task 5) the stored plan_id stays plan_free but
      // effective_plan_id resolves to the trial plan — prefer it for tier flags.
      const planId = data.effective_plan_id || data.plan_id || 'plan_free';
      const tier = PLAN_ID_TO_TIER[planId] || 'free';

      setPlan({
        plan_id: planId,
        plan_type: tier,
        name: data.plan_name || (tier.charAt(0).toUpperCase() + tier.slice(1)),
      });
      setFeatures(data.features || {});
      // usage shape from the endpoint: { films: {current, active, limit, ...},
      // seats: {current, total_members, limit, ...} } — checkLimit/getEntityLimit
      // read usage[key].current/.limit, so this maps directly.
      setUsage(data.usage || {});
      setEntitlements(data.entitlements || {});
      setBilling(data.billing || null);
      setOverLimits(data.over_limits || null);
      setTrial(data.trial || null);
      setAiCredits(data.ai_credits || null);
      setFeatureFlags({});
      setError(null);
    } catch (err) {
      console.error('Failed to fetch workspace entitlements:', err);
      setError(err.message);
      // Set safe free-tier defaults on error
      setPlan({ plan_id: 'plan_free', plan_type: 'free', name: 'Free' });
      setFeatures({ ...FREE_FALLBACK_FEATURES });
      setUsage(null);
      setEntitlements(null);
      setBilling(null);
      setOverLimits(null);
      setTrial(null);
      setAiCredits(null);
    } finally {
      setLoading(false);
    }
  }, [userId, workspaceId]);

  useEffect(() => {
    fetchWorkspaceEntitlements();
  }, [fetchWorkspaceEntitlements]);

  // Check if a feature is enabled based on the workspace's plan
  const hasFeature = useCallback((featureKey) => {
    if (!features) return false;
    return features[featureKey] === true;
  }, [features]);

  // Check if a feature flag is enabled (legacy — workspace entitlements have
  // no global flags; kept so existing fallback chains don't crash)
  const hasFeatureFlag = useCallback((flagKey) => {
    return featureFlags[flagKey] === true;
  }, [featureFlags]);

  // Check usage limit (e.g. 'films', 'seats')
  const checkLimit = useCallback((limitKey) => {
    if (!usage) return { allowed: true, current: 0, limit: -1, unlimited: true };

    const usageData = usage[limitKey];
    if (!usageData) return { allowed: true, current: 0, limit: -1, unlimited: true };

    if (usageData.unlimited || usageData.limit === -1) {
      return { allowed: true, current: usageData.current || 0, limit: -1, unlimited: true };
    }

    const current = usageData.current || 0;
    const limit = usageData.limit || 0;
    return {
      allowed: current < limit,
      current,
      limit,
      remaining: Math.max(0, limit - current),
      unlimited: false
    };
  }, [usage]);

  // Get limit for per-entity limits. The workspace-scoped system does not
  // cap scenes/shots per film (that was a legacy user-plan concept the
  // backend never enforced) — unknown keys resolve to unlimited, matching
  // actual backend behavior.
  const getEntityLimit = useCallback((limitKey) => {
    if (!usage) return { limit: -1, unlimited: true };

    const usageData = usage[limitKey];
    if (!usageData) return { limit: -1, unlimited: true };

    return {
      limit: usageData.limit,
      unlimited: usageData.unlimited || usageData.limit === -1
    };
  }, [usage]);

  // Refresh features (e.g., after an upgrade payment or creating a resource)
  const refreshFeatures = useCallback(() => {
    return fetchWorkspaceEntitlements();
  }, [fetchWorkspaceEntitlements]);

  const value = useMemo(() => ({
    plan,
    features,
    usage,
    entitlements,
    billing,
    overLimits,
    trial,
    aiCredits,
    featureFlags,
    loading,
    error,
    hasFeature,
    hasFeatureFlag,
    checkLimit,
    getEntityLimit,
    refreshFeatures,
    isPro: plan?.plan_type === 'pro',
    isStudio: plan?.plan_type === 'studio',
    isFree: !plan || plan?.plan_type === 'free'
  }), [
    plan,
    features,
    usage,
    entitlements,
    billing,
    overLimits,
    trial,
    aiCredits,
    featureFlags,
    loading,
    error,
    hasFeature,
    hasFeatureFlag,
    checkLimit,
    getEntityLimit,
    refreshFeatures
  ]);

  return (
    <FeatureContext.Provider value={value}>
      {children}
    </FeatureContext.Provider>
  );
};

export const useFeatures = () => {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeatureProvider');
  }
  return context;
};

// Convenience hook for checking a single feature
export const useFeature = (featureKey) => {
  const { hasFeature, loading, plan } = useFeatures();
  return {
    enabled: hasFeature(featureKey),
    loading,
    planName: plan?.name || 'Free',
    planType: plan?.plan_type || 'free'
  };
};

// Convenience hook for checking usage limits
export const useLimit = (limitKey) => {
  const { checkLimit, getEntityLimit, loading, plan } = useFeatures();

  // For aggregate limits (films, seats)
  if (['films', 'seats', 'production_houses'].includes(limitKey)) {
    return {
      ...checkLimit(limitKey),
      loading,
      planName: plan?.name || 'Free'
    };
  }

  // For per-entity limits
  return {
    ...getEntityLimit(limitKey),
    loading,
    planName: plan?.name || 'Free'
  };
};

export default FeatureContext;
